import { useState, useEffect } from "react";
import { Badge, Button, Drawer, Dropdown, Layout, Menu, Popover } from "antd";
import {
  buildHeaderMenuItems,
  leftMenuItems,
  bottomMenuItems,
  bottomExtraMenuItems
} from "../../../shared/menuItems";
import { useLocation, useNavigate } from "react-router-dom";
import type { MenuProps } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/types";
import globalUtils from "../../../utils/globalUtils";
import ROUTES from "../../../routes";
import { resetAll } from "../../../store/slice/globalSlice";
import { baseApi } from "../../../services/base";
import Icon from "../../Icon";
import HrnAvatar from "../../HrnAvatar";
import useScreenSize from "../../../hooks/useScreenSize";
import { UserType } from "../../../shared/constants";
import { useTranslation } from "react-i18next";

const { Header, Content, Sider } = Layout;
const siderWidth = 220;

const AppNavbar = ({ children }: { children: JSX.Element }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { screenSize, isMobile } = useScreenSize();
  const { loggedInUser, unreadNotificationCount } =
    useSelector((state: RootState) => state.global);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [bottomMenuLength, setBottomMenuLength] = useState(3);
  const handleNavMenu: MenuProps["onClick"] = (e) => {
    if (isMobile) setMobileDrawerOpen(false);
    navigate(e.key);
  };

  const handleMobileBottomMenu = (item: { [key: string]: string }) => {
    navigate(item.path);
  };

  const isMobileBottomMenuActive = (item: { [key: string]: string }) => {
    return location.pathname === item.path ? true : false;
  };

  const handleHeaderMenuClick: MenuProps["onClick"] = (e) => {
    switch (e.key) {
      case "SETTINGS":
        navigate(ROUTES.PATHS.SERIALNO_SETTINGS);
        break;
      case "PROFILE":
        navigate(ROUTES.PATHS.PROFILE);
        break;
      case "LOGOUT":
        globalUtils.removeTokens();
        dispatch(resetAll());
        dispatch(baseApi.util.resetApiState());
        navigate(ROUTES.PATHS.LOGIN);
        break;
      default:
        break;
    }
  };

  const menuProps = {
    items: buildHeaderMenuItems(i18n, loggedInUser),
    onClick: handleHeaderMenuClick,
  };

  const getMenuSelectedKeys = () => {
    const pathSnippets = location.pathname.split("/").filter((i) => i);
    const parentRoute = `/${pathSnippets.slice(0, 2).join("/")}`;
    return parentRoute;
  };

  useEffect(() => {
    if (screenSize.width < 1280 && !isMobile) setCollapsed(true);
    else setCollapsed(false);
    if (screenSize.width > 570)
      setBottomMenuLength(6);
    else if (screenSize.width > 520)
      setBottomMenuLength(5);
    else if (screenSize.width > 450)
      setBottomMenuLength(4);
    else setBottomMenuLength(3);
  }, [screenSize.width]);

  const onMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };
  const bottomExtraMenus = bottomExtraMenuItems(location.pathname, i18n, loggedInUser, bottomMenuLength);
  const bottomMenus = bottomMenuItems(location.pathname, i18n, loggedInUser, bottomMenuLength);
  const bottomMenu = () => {
    return <>
      {bottomMenus.map((item: any) => (
        <div
          key={item.path}
          className={`${item.className} flex flex-col pl-[5px] pr-[5px] min-w-[64px] items-center justify-center`}
          onClick={() => handleMobileBottomMenu(item)}
        >
          <Icon
            icon={item.icon}
            className="h-6 w-6"
          />
          <div
            className={`text-[13px] text-center ${isMobileBottomMenuActive(item)
              ? "font-semibold"
              : "font-medium"
              }`}
          >
            {item.label}
          </div>
        </div>
      ))}
      {bottomExtraMenus.length > 0 && <div className={`flex flex-col items-center justify-center`}>
        <Dropdown className="w-[90%] h-[90%] border-0" placement="topRight" menu={{
          items: bottomExtraMenus.map((item: any) => (
            {
              label: item.label,
              key: item.path,
              icon: <Icon icon={item.icon} className="h-6 w-6" />,
              path: item.path
            })),
          onClick: (item: any) => handleMobileBottomMenu({ ...item, path: item.key })
        }} >
          <Button className="default">
            <Icon icon={"menu"} className="h-6 w-6" />,
          </Button>
        </Dropdown>
      </div >}
    </>
  };

  return (
    <>
      <Layout className="min-h-screen">
        <Header className="pl-4 pr-6 fixed top-0 inset-x-0 z-[1] flex justify-between items-center bg-white resetborder-b border-gainsboro">
          <img
            src="/serialman.png"
            className={`cursor-pointer ${isMobile ? "w-[120px] h-[30px]" : "w-[140px] h-[35px]"}`}
            onClick={() =>
              navigate(ROUTES.PATHS.SL_HOME)
            }
          />

          <div className="flex gap-x-4 items-center">
            {loggedInUser &&
              (<div
                className="cursor-pointer"
                onClick={() => navigate(ROUTES.PATHS.NOTIFICATIONS)}
              >
                <Badge
                  count={unreadNotificationCount}
                  offset={[-3, 0]}
                  color="#D86800"
                  style={{ margin: 0, padding: 0 }}
                  className="flex items-center"
                >
                  <Icon
                    icon="notifications"
                    className="h-6 w-7 cursor-pointer"
                  />
                </Badge>
              </div>)}
            {loggedInUser && (
              <Dropdown
                menu={menuProps}
                trigger={["click"]}
                placement="bottomLeft"
                overlayStyle={{ minWidth: 224 }}
              >
                <div className="flex flex-row-reverse items-center gap-3 cursor-pointer">
                  <div>
                    <HrnAvatar
                      size={42}
                      photo={loggedInUser.profileData?.photograph}
                      className="top-4"
                    />
                  </div>

                  <div className="text-right space-y-0.5">
                    <div className="text-[15.5px] font-bold opacity-90 leading-none">
                      {loggedInUser?.name}
                    </div>
                    <div className="text-xs opacity-50">
                      {loggedInUser?.email}
                    </div>
                  </div>

                </div>
              </Dropdown>
            )}
            {/* {loggedInUser && (
              <Popover content={"HrnSoft apps"}>
                <Icon icon="apps" className="apps-icon h-10 w-8 cursor-pointer mt-2" />
              </Popover>
            )} */}
          </div>

        </Header>
        <Layout hasSider className="bg-lotion">
          <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            width={siderWidth}
            className={`resetborder-r border-gainsboro hide-scrollbar ${isMobile ? "hidden" : ""
              }`}
            style={{
              background: "white",
              overflow: "auto",
              position: "fixed",
              left: 0,
              top: 64,
              bottom: 0,
            }}
          >
            <Menu
              selectedKeys={[getMenuSelectedKeys()]}
              mode="inline"
              defaultOpenKeys={[]}
              items={leftMenuItems(location.pathname, i18n, loggedInUser)}
              className="pt-3 pb-6 sm:py-6"
              onClick={handleNavMenu}
            />
          </Sider>
          <Layout
            style={{
              marginLeft: isMobile ? 0 : collapsed ? 79.2 : siderWidth,
              marginTop: 64,
              marginBottom: isMobile ? 64 : 0,
              transition: "all 0.5s ease 0s",
            }}
          >
            <Content className="bg-lotion py-4 md:py-6 px-4 md:px-6 lg:px-10">
              {loggedInUser && children}
            </Content>
          </Layout>
        </Layout>
        {isMobile && (
          <div className="bg-white h-16 fixed bottom-0 inset-x-0 resetborder-t border-gainsboro">
            <div className="flex gap-x-1 h-full">{bottomMenu()}</div>
          </div>
        )}
      </Layout>

      {/* MOBILE DRAWER: *** NOT USING NOW */}
      <Drawer
        title="IDP"
        placement="left"
        width={siderWidth}
        onClose={onMobileDrawerClose}
        open={mobileDrawerOpen}
        style={{ margin: 0, padding: 0 }}
      >
        <Menu
          selectedKeys={[getMenuSelectedKeys()]}
          mode="inline"
          defaultOpenKeys={[]}
          items={leftMenuItems(location.pathname, i18n, loggedInUser)}
          className="pt-3 pb-6 sm:py-6"
          onClick={handleNavMenu}
        />
      </Drawer>
    </>
  );
};

export default AppNavbar;
